/**
 * The items behind each What's new count — which issues were opened or
 * completed, which documents arrived — for the spotlight layout the widget
 * uses when only one or two counts are non-zero. Built from the same rows and
 * the same `issueMetricKey` rule as the counts, so a tile's list is always
 * what its number counts (newest first, one entry per counted key).
 */

import { issueSubject } from "./forYou";
import {
  ISSUE_METRICS,
  isNewLineage,
  issueMetricKey,
  rowsInWindow,
  type IsDoneStatus,
  type IssueMetric,
  type RecentChangeWindow,
  type RecentDocumentRow,
  type RecentIssueActivityRow,
} from "./summary";

export interface IssueExample {
  kind: "issue";
  key: string;
  at: string;
  issueId: number;
  /** "#12 Title". */
  subject: string;
  /** Who did it, from the trigger's snapshot; empty for a system change. */
  actorName: string;
}

export interface DocumentExample {
  kind: "document";
  key: string;
  at: string;
  document: RecentDocumentRow;
}

export type DocumentMetric = "added" | "newVersions";

export function issueExamples(
  rows: RecentIssueActivityRow[],
  now: number,
  window: RecentChangeWindow,
  isDone: IsDoneStatus
): Record<IssueMetric, IssueExample[]> {
  const inWindow = rowsInWindow(rows, now, window);
  const out = {} as Record<IssueMetric, IssueExample[]>;
  for (const metric of ISSUE_METRICS) {
    const seen = new Set<string | number>();
    const list: IssueExample[] = [];
    // Rows arrive newest-first, so the first row per key is its latest.
    for (const row of inWindow) {
      const key = issueMetricKey(metric, row, isDone);
      if (key === null || seen.has(key)) continue;
      seen.add(key);
      const actor = row.metadata?.actor_name;
      list.push({
        kind: "issue",
        key: `${metric}:${key}`,
        at: row.created_at,
        issueId: row.issue_id,
        subject: issueSubject(row.issue_number, row.issue_title),
        actorName: typeof actor === "string" ? actor : "",
      });
    }
    out[metric] = list;
  }
  return out;
}

export function documentExamples(
  rows: RecentDocumentRow[],
  now: number,
  window: RecentChangeWindow
): Record<DocumentMetric, DocumentExample[]> {
  const out: Record<DocumentMetric, DocumentExample[]> = { added: [], newVersions: [] };
  for (const row of rowsInWindow(rows, now, window)) {
    const metric: DocumentMetric = isNewLineage(row) ? "added" : "newVersions";
    out[metric].push({
      kind: "document",
      key: `${metric}:${row.id}`,
      at: row.created_at,
      document: row,
    });
  }
  return out;
}
